import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { FaSpinner } from 'react-icons/fa';

const ShippingInfo = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    shipping: {
      countryRegion: '',
      contactInformation: {
        contactName: '',
        phoneNumber: '',
        email: ''
      },
      shippingAddress: {
        address: '',
        streetHouseApartment: '',
        state: '',
        city: '',
        zipCode: ''
      }
    }
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
    if (user) {
      setFormData({
        shipping: {
          countryRegion: user.address?.countryRegion || '',
          contactInformation: {
            contactName: `${user.firstName} ${user.lastName}`,
            phoneNumber: user.phoneNumber || '',
            email: user.email || ''
          },
          shippingAddress: {
            address: user.address?.streetAddress || '',
            streetHouseApartment: user.address?.streetHouseApartment || '',
            state: user.address?.state || '',
            city: user.address?.city || '',
            zipCode: user.address?.zipCode || ''
          }
        }
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const fields = name.split('.');
    
    setFormData(prevState => {
      let newState = { ...prevState };
      let current = newState;
      
      for (let i = 0; i < fields.length - 1; i++) {
        current = current[fields[i]];
      }
      current[fields[fields.length - 1]] = value;
      
      return newState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const loadingToast = toast.loading('Updating shipping information...');

    try {
      const token = localStorage.getItem('AficFreshtoken');
      const response = await axios.put(
        'https://afric-fresh-backend.onrender.com/user/update-shipping'
        // 'http://localhost:3005/user/update-shipping'
        // 
        ,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // In your handleSubmit function in ShippingInfo.js
      if (response.data.success) {
        // Update local storage
        const currentUser = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
        const updatedUser = {
          ...currentUser,
          address: {
            streetAddress: formData.shipping.shippingAddress.address,
            city: formData.shipping.shippingAddress.city,
            countryRegion: formData.shipping.countryRegion,
            zipCode: formData.shipping.shippingAddress.zipCode,
            state: formData.shipping.shippingAddress.state, // Add state
            streetHouseApartment: formData.shipping.shippingAddress.streetHouseApartment, // Add streetHouseApartment
          },
          phoneNumber: formData.shipping.contactInformation.phoneNumber
        };
        localStorage.setItem('AficFreshuserDetails', JSON.stringify(updatedUser));
        
        setIsEditing(false);
        toast.dismiss(loadingToast);
        toast.success('Shipping information updated successfully');
      }
    } catch (error) {
      console.error('Error updating shipping info:', error);
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.message || 'Failed to update shipping information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    const user = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
    if (user) {
      setFormData({
        shipping: {
          countryRegion: user.address?.countryRegion || '',
          contactInformation: {
            contactName: `${user.firstName} ${user.lastName}`,
            phoneNumber: user.phoneNumber || '',
            email: user.email || ''
          },
          shippingAddress: {
            address: user.address?.streetAddress || '',
            streetHouseApartment: user.address?.streetHouseApartment,
            state: user.address?.state || '',
            city: user.address?.city || '',
            zipCode: user.address?.zipCode || ''
          }
        }
      });
    }
    toast.success('Changes cancelled');
  };

  return (
    <>
    <Toaster position="top-right" />
    <div className="ml-10 max-sm:ml-0 max-sm:p-1 max-sm:mt-24 p-6 bg-white rounded-lg shadow-lg mt-8 w-screen">
      
      <div className="flex justify-between border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Shipping Information</h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 max-sm:px-1 py-1 py-2 bg-[#007D44] text-white rounded-lg hover:bg-[#3eb780] transition-all duration-300 transform hover:-translate-y-0.5"
          >
            Edit Information
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Country Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Country Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country/Region:
              </label>
              <input
                type="text"
                name="shipping.countryRegion"
                value={formData.shipping?.countryRegion || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Contact Information</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name:
              </label>
              <input
                type="text"
                name="shipping.contactInformation.contactName"
                value={formData.shipping?.contactInformation?.contactName || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number:
              </label>
              <input
                type="tel"
                name="shipping.contactInformation.phoneNumber"
                value={formData.shipping?.contactInformation?.phoneNumber || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email:
              </label>
              <input
                type="email"
                name="shipping.contactInformation.email"
                value={formData.shipping?.contactInformation?.email || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Shipping Address</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address:
              </label>
              <input
                type="text"
                name="shipping.shippingAddress.address"
                value={formData.shipping?.shippingAddress?.address || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                House/Apartment/Unit:
              </label>
              <input
                type="text"
                name="shipping.shippingAddress.streetHouseApartment"
                value={formData.shipping?.shippingAddress?.streetHouseApartment || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State:
              </label>
              <input
                type="text"
                name="shipping.shippingAddress.state"
                value={formData.shipping?.shippingAddress?.state || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City:
              </label>
              <input
                type="text"
                name="shipping.shippingAddress.city"
                value={formData.shipping?.shippingAddress?.city || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zip Code:
              </label>
              <input
                type="text"
                name="shipping.shippingAddress.zipCode"
                value={formData.shipping?.shippingAddress?.zipCode || ''}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isEditing ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 border-gray-200'
                } transition-colors duration-200`}
              />
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-300 transform hover:-translate-y-0.5 disabled:bg-green-300 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={handleCancel}
              className="flex-1 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300 transform hover:-translate-y-0.5 disabled:bg-red-300 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
    </>
  );
};

export default ShippingInfo;